import React, { useState } from 'react';
import EndpointCard from './EndpointCard';
import { exportDoc } from '../utils/exportDoc';

const METHODS = ['get', 'post', 'put', 'patch', 'delete', 'head', 'options'];

function groupByTag(paths) {
  const groups = {};
  for (const [path, pathItem] of Object.entries(paths)) {
    for (const method of METHODS) {
      const op = pathItem[method];
      if (!op) continue;
      const tags = op.tags?.length > 0 ? op.tags : ['default'];
      for (const tag of tags) {
        if (!groups[tag]) groups[tag] = [];
        groups[tag].push({ path, method, operation: op });
      }
    }
  }
  return groups;
}

const s = {
  /* stats bar — same pattern as StyleGuard's score row */
  statsBar: {
    display: 'flex', gap: '0',
    border: '1px solid var(--border)', borderRadius: 'var(--radius)',
    overflow: 'hidden', width: 'fit-content', marginBottom: '24px',
  },
  statCell: {
    padding: '10px 20px', borderRight: '1px solid var(--border)',
    textAlign: 'center',
  },
  statNum: {
    fontFamily: '"Geist Mono", monospace', fontWeight: 600, fontSize: '20px',
    color: 'var(--foreground)', lineHeight: 1, display: 'block', marginBottom: '2px',
  },
  statLabel: {
    fontFamily: '"Geist Mono", monospace', fontSize: '10px',
    letterSpacing: '0.08em', textTransform: 'uppercase',
    color: 'var(--muted-foreground)',
  },
  /* title area */
  titleRow: {
    display: 'flex', alignItems: 'flex-start', gap: '12px',
    marginBottom: '12px', flexWrap: 'wrap',
  },
  titleBlock: { flex: 1 },
  h2: {
    fontFamily: 'Geist, sans-serif', fontWeight: 700, fontSize: '22px',
    letterSpacing: '-0.025em', color: 'var(--foreground)', marginBottom: '6px',
  },
  metaRow: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  versionBadge: {
    fontFamily: '"Geist Mono", monospace', fontSize: '11px',
    padding: '2px 8px', borderRadius: 'calc(var(--radius) * 0.6)',
    border: '1px solid var(--border)',
    background: 'var(--secondary)', color: 'var(--muted-foreground)',
  },
  metaDot: { color: 'var(--muted-foreground)', fontSize: '12px' },
  serverLink: {
    fontFamily: '"Geist Mono", monospace', fontSize: '11px',
    color: 'var(--muted-foreground)', textDecoration: 'none',
  },
  description: {
    fontSize: '13px', color: 'var(--muted-foreground)',
    lineHeight: 1.75, maxWidth: '600px', marginBottom: '20px',
  },
  /* action buttons */
  btnRow: { display: 'flex', gap: '8px', flexShrink: 0 },
  btnSecondary: {
    fontFamily: 'Geist, sans-serif', fontSize: '12px', fontWeight: 500,
    padding: '6px 14px', borderRadius: 'var(--radius)',
    border: '1px solid var(--border)',
    background: 'var(--secondary)', color: 'var(--secondary-foreground)',
    cursor: 'pointer', transition: 'background 0.15s',
  },
  btnGhost: {
    fontFamily: 'Geist, sans-serif', fontSize: '12px', fontWeight: 500,
    padding: '6px 14px', borderRadius: 'var(--radius)',
    border: '1px solid var(--border)',
    background: 'transparent', color: 'var(--muted-foreground)',
    cursor: 'pointer',
  },
  divider: { borderBottom: '1px solid var(--border)', marginBottom: '28px', paddingBottom: '24px' },
  /* filter row */
  filterRow: {
    display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap',
  },
  searchInput: {
    fontFamily: 'Geist, sans-serif', fontSize: '13px', flex: 1, minWidth: '180px',
    background: 'var(--card)', border: '1px solid var(--input)',
    borderRadius: 'var(--radius)', color: 'var(--foreground)',
    padding: '7px 12px', outline: 'none',
    transition: 'border-color 0.15s',
  },
  methodSelect: {
    fontFamily: '"Geist Mono", monospace', fontSize: '12px',
    background: 'var(--card)', border: '1px solid var(--input)',
    borderRadius: 'var(--radius)', color: 'var(--foreground)',
    padding: '7px 10px', outline: 'none',
  },
  /* tag group */
  tagHeader: {
    display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px',
  },
  tagLabel: {
    fontFamily: '"Geist Mono", monospace', fontSize: '11px',
    letterSpacing: '0.08em', textTransform: 'uppercase',
    color: 'var(--muted-foreground)',
  },
  tagDivider: { flex: 1, height: '1px', background: 'var(--border)' },
  tagCount: {
    fontFamily: '"Geist Mono", monospace', fontSize: '10px',
    color: 'var(--muted-foreground)',
  },
  emptyState: {
    padding: '40px', textAlign: 'center', fontFamily: 'Geist, sans-serif',
    fontSize: '13px', color: 'var(--muted-foreground)',
    border: '1px dashed var(--border)', borderRadius: 'var(--radius)',
  },
};

export default function DocViewer({ spec, onReset }) {
  const [search, setSearch] = useState('');
  const [filterMethod, setFilterMethod] = useState('all');

  const groups = groupByTag(spec.paths);

  /* method counts */
  const methodCounts = {};
  for (const pathItem of Object.values(spec.paths)) {
    for (const m of METHODS) {
      if (pathItem[m]) methodCounts[m] = (methodCounts[m] || 0) + 1;
    }
  }
  const totalEndpoints = Object.values(methodCounts).reduce((a, b) => a + b, 0);

  const methodVar = (m) => `var(--method-${m})`;

  const filtered = (entries) =>
    entries.filter(({ path, method, operation }) => {
      if (filterMethod !== 'all' && method !== filterMethod) return false;
      const q = search.toLowerCase();
      return !q
        || path.toLowerCase().includes(q)
        || operation.summary?.toLowerCase().includes(q)
        || operation.tags?.some((t) => t.toLowerCase().includes(q));
    });

  return (
    <div>
      {/* ── Doc header ── */}
      <div style={s.divider}>
        <div style={s.titleRow}>
          <div style={s.titleBlock}>
            <h2 style={s.h2}>{spec.info.title}</h2>
            <div style={s.metaRow}>
              <span style={s.versionBadge}>v{spec.info.version}</span>
              {spec.specVersion && (
                <>
                  <span style={s.metaDot}>·</span>
                  <span style={{ ...s.versionBadge, background: 'transparent' }}>
                    OpenAPI {spec.specVersion}
                  </span>
                </>
              )}
              {spec.servers[0]?.url && (
                <>
                  <span style={s.metaDot}>·</span>
                  <a href={spec.servers[0].url} target="_blank" rel="noreferrer" style={s.serverLink}>
                    {spec.servers[0].url} ↗
                  </a>
                </>
              )}
            </div>
          </div>

          <div style={s.btnRow}>
            <button style={s.btnSecondary} onClick={() => exportDoc(spec)}>↓ Export HTML</button>
            <button style={s.btnGhost} onClick={onReset}>← New spec</button>
          </div>
        </div>

        {spec.info.description && (
          <p style={s.description}>{spec.info.description}</p>
        )}

        {/* Stats bar */}
        <div style={s.statsBar}>
          {[
            { label: 'Endpoints', value: totalEndpoints },
            { label: 'Paths', value: Object.keys(spec.paths).length },
            { label: 'Servers', value: spec.servers.length },
          ].map(({ label, value }, i, arr) => (
            <div key={label} style={{
              ...s.statCell,
              borderRight: i < arr.length - 1 || Object.keys(methodCounts).length > 0
                ? '1px solid var(--border)' : 'none',
            }}>
              <span style={s.statNum}>{value}</span>
              <span style={s.statLabel}>{label}</span>
            </div>
          ))}
          {Object.entries(methodCounts).map(([m, count], i, arr) => (
            <div key={m} style={{
              ...s.statCell,
              borderRight: i < arr.length - 1 ? '1px solid var(--border)' : 'none',
            }}>
              <span style={{ ...s.statNum, color: methodVar(m) }}>{count}</span>
              <span style={{ ...s.statLabel, color: methodVar(m) }}>{m.toUpperCase()}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Filters ── */}
      <div style={s.filterRow}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter endpoints…"
          style={s.searchInput}
        />
        <select value={filterMethod} onChange={(e) => setFilterMethod(e.target.value)} style={s.methodSelect}>
          <option value="all">All methods</option>
          {METHODS.map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}
        </select>
      </div>

      {/* ── Endpoint groups ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
        {Object.entries(groups).map(([tag, entries]) => {
          const visible = filtered(entries);
          if (visible.length === 0) return null;
          return (
            <div key={tag}>
              <div style={s.tagHeader}>
                <span style={s.tagLabel}>{tag}</span>
                <div style={s.tagDivider} />
                <span style={s.tagCount}>{visible.length} endpoint{visible.length !== 1 ? 's' : ''}</span>
              </div>
              {visible.map(({ path, method, operation }, i) => (
                <EndpointCard
                  key={`${method}-${path}-${i}`}
                  path={path}
                  method={method}
                  operation={operation}
                  spec={spec}
                />
              ))}
            </div>
          );
        })}

        {Object.values(groups).every((e) => filtered(e).length === 0) && (
          <div style={s.emptyState}>No endpoints match your filter.</div>
        )}
      </div>
    </div>
  );
}
